/**
 * Lectura del libro: de los rangos que devuelve values.batchGet (UNFORMATTED_VALUE, SERIAL_NUMBER)
 * al modelo del dominio. No calcula nada: los valores efectivos los calcula @pf/domain.
 */
import type { Account, BudgetRow, Category, CategoryKind, Movement, Part, Rule } from '@pf/domain';
import { bool, hash12, isBlankRow, monthCell, num, serialToIso, text } from './cells.js';

/** Rangos de datos que se piden en cada sincronización. */
export const DATA_RANGES = {
  movements: 'Movimientos!A1:P', parts: 'Divisiones!A1:K', accounts: 'Cuentas!A1:P', categories: "'Categorías'!A1:E",
  budget: 'Presupuesto!A1:Q', snapshots: 'Patrimonio!A1:E', investments: 'Inversiones!A1:N',
  meta: '_Meta!A1:H', rules: '_Reglas!A1:H', aliases: '_Alias!A1:E', control: '_Control!A1:B60',
} as const;

/** Fila 1 en modo FORMULA, para comprobar que las columnas calculadas siguen intactas. */
/** Pestañas añadidas después de la primera versión del libro: si faltan, la app lo dice y ofrece crearlas. */
export const OPTIONAL_RANGES = { notes: 'Notas!A1:C', planned: 'Previstos!A1:L', orders: '_Pedidos!A1:I' } as const;

export const FORMULA_RANGES = ['Movimientos!A1:P1', 'Divisiones!A1:K1', 'Cuentas!A1:N2', 'Patrimonio!D1', 'Inversiones!G1:N4', '_Control!B3', '_Control!B10'] as const;

export type Values = unknown[][];

export interface WorkbookMovement extends Movement {
  /** Fila en la hoja (1 = cabecera). */
  row: number;
  /** Huella de las celdas que rellena el usuario (A:I y O), para detectar cambios hechos en otro sitio. */
  fingerprint: string;
}

export interface WorkbookPart extends Part { row: number; fingerprint: string }

export interface ParseProblem { tab: string; row: number; message: string }

export interface WorkbookControl {
  version: number | null; archiveRevision: number; archiveFingerprint: string; archivedYears: number[]; archiveJournal: string; yearsWithMovements: number[];
}

export interface Snapshot { month: string; cash: number; investments: number; source: string; row: number }
export interface Flow { date: string; fund: string; amount: number; units: number | null }
export interface Position { fund: string; price: number | null; priceDate: string }
export interface MetaRow { id: string; source: string; sourceRef: string; valueDate: string; balanceAfter: number | null; tricountSentAt: string; tricountAmount: number | null; importedAt: string }
export interface NoteRow { month: string; category: string; note: string; row: number }
/**
 * Gasto previsto (pestaña Previstos): algo que sabes que va a pasar (el seguro en octubre, la inscripción en junio).
 * - month: 1-12; repeat: cada año (desde «from», hasta «to» si lo hay) o solo el año «from».
 * - budget: 'add' lo suma al presupuesto de ese mes; 'included' ya estaba dentro.
 * - linked/unlinked: movimientos confirmados como su pago, o descartados para que no se vuelvan a reconocer solos.
 */
export interface PlannedRow {
  id: string; category: string; month: number; amount: number; concept: string; keyword: string;
  repeat: 'yearly' | 'once'; from: number; to: number | null; budget: 'add' | 'included'; linked: string[]; unlinked: string[]; row: number;
}
/**
 * Una línea de _Pedidos (Amazon): un artículo de un pedido, de un envío (Amazon cobra al enviar) o de un reembolso.
 * - amount: lo de ese artículo (cantidad × precio); null en los reembolsos, que solo dicen el total.
 * - total: el total del pedido, del envío o del reembolso (se repite en cada artículo).
 * - ref: identifica el email del que sale, para no apuntarlo dos veces; movementId: el cobro del banco, cuando se enlace.
 */
export type OrderKind = 'order' | 'shipment' | 'refund';
export interface OrderRow { date: string; kind: OrderKind; orderId: string; item: string; qty: number; amount: number | null; total: number | null; ref: string; movementId: string; row: number }
export const ORDER_KIND_LABEL: Record<OrderKind, string> = { order: 'Pedido', shipment: 'Envío', refund: 'Reembolso' };
export interface AliasRow { account: string; date: string; amount: number; description: string; id: string }

/** Cuenta con lo archivado (Cuentas!H), necesario para el saldo calculado. */
export interface WorkbookAccount extends Account { archived: number;
  /** Uso de la cuenta (Cuentas M), tal cual: '' si no se ha dicho. */
  use: string;
  /** Otras personas de la cuenta (Cuentas O) y cómo se calcula tu parte (Cuentas P). */
  people: string; split: string }

/** Movimiento de la pestaña Archivo (valores ya calculados al archivar). */
export interface ArchivedMovement {
  id: string; date: string; concept: string; amount: number; account: string; category: string; shared: boolean; group: string; note: string;
  month: string; kind: 'expense' | 'income' | 'excluded'; attributable: number;
}

export interface Workbook {
  headers: Record<string, string[]>;
  movements: WorkbookMovement[];
  parts: WorkbookPart[];
  accounts: WorkbookAccount[];
  categories: Category[];
  budget: BudgetRow[];
  snapshots: Snapshot[];
  flows: Flow[];
  positions: Position[];
  meta: MetaRow[];
  rules: Rule[];
  aliases: AliasRow[];
  /** Notas del mes por categoría (pestaña Notas). */
  notes: NoteRow[];
  /** Gastos previstos (pestaña Previstos; vacía si aún no existe). */
  planned: PlannedRow[];
  /** Pedidos de Amazon (pestaña _Pedidos; vacía si aún no existe). */
  orders?: OrderRow[];
  /** Pestañas opcionales que no existen en el libro (p. ej. 'Notas' en libros anteriores). */
  missingTabs: string[];
  control: WorkbookControl;
  problems: ParseProblem[];
  /** Ids en filas sin movimiento (p. ej. escritos por error en filas vacías): se pueden limpiar. */
  orphanIds: Array<{ row: number; id: string }>;
}

const KIND: Record<string, CategoryKind> = { gasto: 'expense', ingreso: 'income', 'no cuenta': 'excluded' };

export function movementFingerprint(m: Pick<Movement, 'date' | 'concept' | 'amount' | 'account' | 'category' | 'shared' | 'group' | 'note' | 'month' | 'id'>): string {
  return hash12(JSON.stringify([m.date, m.concept, m.amount, m.account, m.category, m.shared, m.group, m.note, m.month, m.id]));
}

/**
 * ¿La fila tiene un movimiento? Solo si tiene fecha, concepto o importe. Las casillas "Compartido" vacías
 * (FALSO) y las demás columnas no cuentan: el libro trae 3.000 filas preparadas con casilla.
 */
export function hasMovement(r: unknown[] | undefined): boolean {
  return !!r && !isBlankRow([r[0], r[1], r[2]]);
}

/** Una fila de Movimientos (A:P) → movimiento, o null si la fila no tiene movimiento. */
export function movementFromRow(r: unknown[], row: number): WorkbookMovement | null {
  if (!hasMovement(r)) return null;
  const m = { id: text(r[14]), date: serialToIso(r[0]), concept: text(r[1]), amount: num(r[2]) ?? 0, account: text(r[3]), category: text(r[4]),
    shared: bool(r[5]), group: text(r[6]), note: text(r[7]), month: monthCell(r[8]) };
  return { ...m, row, fingerprint: movementFingerprint(m) };
}

export function partFingerprint(p: Part): string {
  return hash12(JSON.stringify([p.movementId, p.category, p.amount, p.note]));
}

/** Rangos leídos. Las pestañas opcionales: null = no existe en el libro; sin la clave = no se han pedido. */
export type WorkbookRanges = Record<keyof typeof DATA_RANGES, Values> & Partial<Record<keyof typeof OPTIONAL_RANGES, Values | null>>;

export function parseWorkbook(ranges: WorkbookRanges): Workbook {
  const problems: ParseProblem[] = [];
  const headers: Record<string, string[]> = {};
  const body = (tab: string, v: Values | undefined) => { headers[tab] = (v?.[0] ?? []).map(text); return (v ?? []).slice(1); };

  const movements: WorkbookMovement[] = [];
  const orphanIds: Array<{ row: number; id: string }> = [];
  body('Movimientos', ranges.movements).forEach((r, i) => {
    const row = i + 2;
    const m = movementFromRow(r, row);
    if (!m) { if (text(r[14])) orphanIds.push({ row, id: text(r[14]) }); return; }
    if (!m.date) problems.push({ tab: 'Movimientos', row, message: 'Fecha no válida' });
    if (num(r[2]) === null) problems.push({ tab: 'Movimientos', row, message: 'Importe no válido' });
    movements.push(m);
  });

  const parts: WorkbookPart[] = [];
  body('Divisiones', ranges.parts).forEach((r, i) => {
    if (isBlankRow(r.slice(0, 4))) return;
    const p = { movementId: text(r[0]), category: text(r[1]), amount: num(r[2]) ?? 0, note: text(r[3]) };
    parts.push({ ...p, row: i + 2, fingerprint: partFingerprint(p) });
  });

  const accounts: WorkbookAccount[] = body('Cuentas', ranges.accounts).filter(r => text(r[0])).map(r => ({
    name: text(r[0]), bank: text(r[1]), type: text(r[2]), share: num(r[3]) ?? 1, inNetWorth: bool(r[4]), opening: num(r[5]) ?? 0,
    openingDate: serialToIso(r[6]), archived: num(r[7]) ?? 0, bankBalance: num(r[9]), bankBalanceDate: serialToIso(r[10]), use: text(r[12]), people: text(r[14]), split: text(r[15]),
  }));

  const categories: Category[] = body('Categorías', ranges.categories).filter(r => text(r[0])).map(r => {
    const nature = text(r[2]).toLowerCase();
    return { name: text(r[0]), kind: KIND[text(r[1]).toLowerCase()] ?? 'expense', nature: nature === 'fijo' ? 'fixed' : nature === 'variable' ? 'variable' : '',
      keywords: text(r[3]).split(',').map(k => k.trim()).filter(Boolean),
      // Columna E (opcional, libros anteriores sin ella): a partir de qué día cuenta en el mes siguiente
      nextMonthFrom: (() => { const n = num(r[4]); return n !== null && n >= 1 && n <= 31 ? Math.round(n) : null; })() };
  });

  const budget: BudgetRow[] = body('Presupuesto', ranges.budget).filter(r => num(r[0]) !== null && text(r[1])).map(r => ({
    year: num(r[0])!, category: text(r[1]), monthly: num(r[2]) ?? 0,
    overrides: Array.from({ length: 12 }, (_, i) => num(r[3 + i])), warn: bool(r[15]), note: text(r[16]),
  }));

  const snapshots: Snapshot[] = body('Patrimonio', ranges.snapshots).map((r, i) => ({ r, i })).filter(({ r }) => monthCell(r[0])).map(({ r, i }) => ({
    month: monthCell(r[0]), cash: num(r[1]) ?? 0, investments: num(r[2]) ?? 0, source: text(r[4]), row: i + 2 }));

  const inv = body('Inversiones', ranges.investments);
  const flows: Flow[] = inv.filter(r => serialToIso(r[0]) && num(r[2]) !== null).map(r => ({ date: serialToIso(r[0]), fund: text(r[1]), amount: num(r[2])!, units: num(r[3]) }));
  const positions: Position[] = inv.filter(r => text(r[5])).map(r => ({ fund: text(r[5]), price: num(r[7]), priceDate: serialToIso(r[8]) }));

  const meta: MetaRow[] = body('_Meta', ranges.meta).filter(r => text(r[0])).map(r => ({
    id: text(r[0]), source: text(r[1]), sourceRef: text(r[2]), valueDate: serialToIso(r[3]), balanceAfter: num(r[4]),
    tricountSentAt: serialToIso(r[5]), tricountAmount: num(r[6]), importedAt: serialToIso(r[7]) }));

  const rules: Rule[] = body('_Reglas', ranges.rules).filter(r => text(r[6])).map(r => {
    const dir = text(r[5]).toLowerCase();
    return { priority: num(r[0]), account: text(r[1]), contains: text(r[2]), regex: text(r[3]), amountEquals: num(r[4]),
      direction: dir === 'expense' || dir === 'gasto' ? 'expense' : dir === 'income' || dir === 'ingreso' ? 'income' : '', category: text(r[6]),
      active: r[7] === '' || r[7] === undefined ? true : bool(r[7]) };
  });

  const aliases: AliasRow[] = body('_Alias', ranges.aliases).filter(r => text(r[4])).map(r => ({
    account: text(r[0]), date: serialToIso(r[1]), amount: num(r[2]) ?? 0, description: text(r[3]), id: text(r[4]) }));

  const c = ranges.control ?? [];
  const cell = (label: string) => c.find(r => text(r[0]).toLowerCase() === label.toLowerCase())?.[1];
  const yearsIdx = c.findIndex(r => text(r[0]).toLowerCase() === 'años con movimientos');
  const control: WorkbookControl = {
    version: num(cell('Versión del libro')),
    archiveRevision: num(cell('Revisión del archivo')) ?? 0,
    archiveFingerprint: text(cell('Huella del archivo')),
    archivedYears: text(cell('Años archivados')).split(',').map(s => Number(s.trim())).filter(n => n > 0),
    archiveJournal: text(cell('Diario del archivo')),
    yearsWithMovements: yearsIdx < 0 ? [] : c.slice(yearsIdx).map(r => num(r[1])).filter((n): n is number => n !== null),
  };

  // Solo se comprueban las cabeceras de Notas si la pestaña se ha leído (existe)
  const notes: NoteRow[] = (Array.isArray(ranges.notes) ? body('Notas', ranges.notes) : []).map((r, i) => ({ month: monthCell(r[0]), category: text(r[1]), note: text(r[2]), row: i + 2 }))
    .filter(n => /^\d{4}-\d{2}$/.test(n.month) && n.category && n.note);
  const missingTabs = ranges.notes === null ? ['Notas'] : [];
  // Previstos: la pestaña se crea al guardar el primero, así que no tenerla no es un problema
  const ids = (v: unknown) => text(v).split(/[,\s]+/).filter(Boolean);
  const planned: PlannedRow[] = (Array.isArray(ranges.planned) ? ranges.planned.slice(1) : []).map((r, i) => ({
    id: text(r[0]), category: text(r[1]), month: num(r[2]) ?? 0, amount: Math.abs(num(r[3]) ?? 0), concept: text(r[4]), keyword: text(r[5]),
    repeat: /solo/i.test(text(r[6])) ? 'once' as const : 'yearly' as const, from: num(r[7]) ?? 0, to: num(r[8]),
    budget: /incluid/i.test(text(r[9])) ? 'included' as const : 'add' as const, linked: ids(r[10]), unlinked: ids(r[11]), row: i + 2,
  })).filter(p => p.id && p.category && p.month >= 1 && p.month <= 12 && p.amount > 0 && p.from > 0);

  const kindOf = (v: unknown): OrderKind | null => {
    const t = text(v).toLowerCase();
    return t === 'pedido' ? 'order' : t === 'envío' || t === 'envio' ? 'shipment' : t === 'reembolso' ? 'refund' : null;
  };
  const orders: OrderRow[] = (Array.isArray(ranges.orders) ? ranges.orders.slice(1) : []).flatMap((r, i): OrderRow[] => {
    const kind = kindOf(r[1]), date = serialToIso(r[0]);
    return kind && date && text(r[2]) ? [{ date, kind, orderId: text(r[2]), item: text(r[3]), qty: num(r[4]) ?? 1, amount: num(r[5]), total: num(r[6]),
      ref: text(r[7]), movementId: text(r[8]), row: i + 2 }] : [];
  });

  return { headers, movements, parts, accounts, categories, budget, snapshots, flows, positions, meta, rules, aliases, notes, planned, orders, missingTabs, control, problems, orphanIds };
}

const KIND_ARCHIVE: Record<string, ArchivedMovement['kind']> = { gasto: 'expense', ingreso: 'income', 'no cuenta': 'excluded' };

export function parseArchive(values: Values): ArchivedMovement[] {
  return (values ?? []).slice(1).filter(r => !isBlankRow(r)).map(r => ({
    id: text(r[11]), date: serialToIso(r[0]), concept: text(r[1]), amount: num(r[2]) ?? 0, account: text(r[3]), category: text(r[4]),
    shared: bool(r[5]), group: text(r[6]), note: text(r[7]), month: monthCell(r[8]), kind: KIND_ARCHIVE[text(r[9]).toLowerCase()] ?? 'expense',
    attributable: num(r[10]) ?? 0,
  }));
}
